import AccessTime from '@mui/icons-material/AccessTime';
import GpsFixed from '@mui/icons-material/GpsFixed';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import Navigation from '@mui/icons-material/Navigation';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { IdleIcon } from '~components/Common/GpsStatusIcon';
import { Status as JobStatus } from '~components/Job/Status';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import { NextBillionAssetLocation } from '~hooks/useNextBillionAssetLocationHistories/models';
import { Nullable } from '~types/Nullable';
import { clampSpeed } from '~utils/speed';

const emDash = '-';

const convertSpeedToMph = (speed: Nullable<string>): string => {
  if (!speed) return emDash;
  const parsed = parseFloat(speed);
  if (isNaN(parsed)) return emDash;
  return `${Math.round(clampSpeed(parsed) * 2.237)} mph`;
};

const convertBearingToCardinal = (bearing: Nullable<string>): string => {
  if (!bearing) return emDash;
  const parsed = parseFloat(bearing);
  if (isNaN(parsed)) return emDash;

  if (parsed >= 337.5 || parsed < 22.5) return 'N';
  if (parsed >= 22.5 && parsed < 67.5) return 'NE';
  if (parsed >= 67.5 && parsed < 112.5) return 'E';
  if (parsed >= 112.5 && parsed < 157.5) return 'SE';
  if (parsed >= 157.5 && parsed < 202.5) return 'S';
  if (parsed >= 202.5 && parsed < 247.5) return 'SW';
  if (parsed >= 247.5 && parsed < 292.5) return 'W';
  return 'NW';
};

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={(theme) => ({
          bgcolor: theme.brandV2.colors.treadGray8,
          borderRadius: '50%',
          p: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray3 })}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value ?? emDash}
        </Typography>
      </Box>
    </Box>
  );
}

interface StatColumnProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function StatColumn({ icon, label, value }: StatColumnProps) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
      >
        {icon}
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray3 })}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export const TruckHoverPopoverContent = observer(
  ({ truckLocation }: { truckLocation: NextBillionAssetLocation }) => {
    const { singular } = useDispatchNumberLabel();

    return (
      <Box
        sx={{
          maxWidth: '340px',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header Section - Dark */}
        <Box
          sx={(theme) => ({
            bgcolor: theme.brandV2.colors.treadBlack,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          })}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              p: 0.75,
              display: 'flex',
            }}
          >
            <LocalShippingOutlined
              sx={(theme) => ({ color: theme.semantic.textInverse, fontSize: '20px' })}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                color: theme.semantic.textInverse,
                fontSize: '14px',
                fontWeight: 'bold',
              })}
            >
              {truckLocation.equipment?.company_share?.external_id ??
                truckLocation.equipment?.name}
            </Typography>
            {truckLocation.driverName && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {truckLocation.driverName}
              </Typography>
            )}
            {truckLocation.vendorName && (
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}
              >
                {truckLocation.vendorName}
              </Typography>
            )}
          </Box>
          {truckLocation.loadState && (
            <JobStatus status={truckLocation.loadState as JobState} />
          )}
          {truckLocation.idle && (
            <Tooltip title={t('common.gps_status.idle')}>
              <Box sx={{ display: 'flex' }}>
                <IdleIcon size={20} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Stats Row - Light Gray */}
        <Box
          sx={(theme) => ({
            bgcolor: theme.brandV2.colors.treadGray8,
            p: 1.5,
            display: 'flex',
            justifyContent: 'space-around',
          })}
        >
          <StatColumn
            icon={<Navigation sx={{ fontSize: '16px' }} />}
            label={t('live_map.truck_popover.speed')}
            value={convertSpeedToMph(truckLocation.speed)}
          />
          <StatColumn
            icon={<GpsFixed sx={{ fontSize: '16px' }} />}
            label={t('live_map.truck_popover.heading')}
            value={convertBearingToCardinal(truckLocation.bearing)}
          />
          <StatColumn
            icon={<AccessTime sx={{ fontSize: '16px' }} />}
            label={t('live_map.truck_popover.updated')}
            value={truckLocation.occurredAt.format('hh:mm A')}
          />
        </Box>

        {/* Details Section - White */}
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <DetailRow
            icon={<LabelOutlined sx={{ fontSize: '16px' }} />}
            label={t('live_map.truck_popover.license_plate')}
            value={truckLocation.equipment?.license_number}
          />
          {truckLocation.driverPhoneNumber && (
            <DetailRow
              icon={<PhoneOutlined sx={{ fontSize: '16px' }} />}
              label={t('live_map.truck_popover.phone')}
              value={truckLocation.driverPhoneNumber}
            />
          )}
          <DetailRow
            icon={<LocalShippingOutlined sx={{ fontSize: '16px' }} />}
            label={singular('live_map.truck_popover.dispatch_number')}
            value={truckLocation.dispatchNumber?.name}
          />
        </Box>
      </Box>
    );
  },
);
